import i18n from "@/i18n/i18n";

export const parseSafeHttpUrl = (
  value: unknown,
  base: string = window.location.origin,
): URL | null => {
  if (typeof value !== "string" || !value.trim()) return null;

  try {
    const parsedUrl = new URL(value.trim(), base);
    if (!["http:", "https:"].includes(parsedUrl.protocol)) return null;
    return parsedUrl;
  } catch {
    return null;
  }
};

export const removeCommasAndLowercase = (address: string): string =>
  address.replace(/,/g, "").toLowerCase().trim().replace(/\s+/g, " ");

export const maskPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber.length <= 3) return phoneNumber;
  return "*".repeat(phoneNumber.length - 3) + phoneNumber.slice(-3);
};

export const normalizePhone = (phone: string | null | undefined): string =>
  (phone ?? "").replace(/\s+/g, "");

export const stripLocalePrefixFromPath = (
  path: string,
  languageParam?: string,
): string => {
  if (!languageParam) return path;
  const localePrefix = `/${languageParam}`;
  return path.toLowerCase().startsWith(`${(localePrefix.toLowerCase())}/`)
      ? path.slice(localePrefix.length)
      : path;
};

export const unwrapApiData = <T extends object>(
  data: { data?: T } | T | null | undefined,
): T =>
  data != null && typeof data === "object" && "data" in data
    ? (((data as { data?: T }).data ?? {}) as T)
    : ((data ?? {}) as T);

export const formatDate = (iso: string): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString(i18n.resolvedLanguage);
};

export const getDisplayName = (parts: {
  firstName?: string;
  lastName?: string;
}): string => {
  const { firstName, lastName } = parts;
  const nameParts = [firstName, lastName].filter(Boolean);
  return nameParts.join(" ");
};

// Converts an ISO date string to a localized "HH:MM AM/PM TZ, Month DD, YYYY" string
export const formatLastModified = (iso: string): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";

  const locale = i18n.resolvedLanguage;

  const time = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  const timezone =
    new Intl.DateTimeFormat(locale, { timeZoneName: "shortOffset" })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value ?? "";

  const datePart = new Intl.DateTimeFormat(locale, {
    month: "long",
    day: "2-digit",
    year: "numeric",
  }).format(date);

  return `${time} ${timezone}, ${datePart}`;
};
